import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    AvField,
    AvForm,
    AvGroup,
} from 'availity-reactstrap-validation';
import { withNamespaces } from 'react-i18next';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
    Button,
    Col,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
} from 'reactstrap';

import DocumentManagementService, {
    getDocumentApiError,
} from 'src/modules/3rd-party-management/apis/admin/DocumentManagementService';
import OrganizationProductsService from 'src/modules/3rd-party-management/apis/admin/OrganizationProductsService';
import CommonService from 'src/modules/3rd-party-management/apis/common/CommonService';
import EudrDeclarationService from 'src/modules/3rd-party-management/apis/thirdparty/EudrDeclarationService';
import OwnThirdpartyUsersService from 'src/modules/3rd-party-management/apis/admin/OwnThirdpartyUsersService';
import ThirdpartyUsersService from 'src/modules/3rd-party-management/apis/common/ThirdpartyUsersService';
import {
    DOCUMENT_FILE_EXTENSIONS,
    DOCUMENT_FILE_MAX_BYTES,
    ThirdPartyDocumentCategories,
    ThirdPartyDocumentConfidentialityLevels,
    ThirdPartyDocumentTypes,
} from 'src/modules/3rd-party-management/constants';

const ALL_LEGAL_ENTITIES = 'all';

const normalizeLegalEntity = (entity) => ({
    value: String(entity.value ?? entity.id),
    label: entity.label ?? entity.title ?? entity.name,
});

const productLabel = (product) => (
    product?.productName
    || product?.product_name
    || product?.name?.name
    || (typeof product?.name === 'string' ? product.name : '')
    || String(product?.id ?? '')
);

const getDocumentProductOption = (document) => {
    const productId = document?.productId || document?.product_id;

    if (!productId) {
        return null;
    }

    return {
        value: productId,
        label: document.productName || document.product_name || String(productId),
    };
};

const getSupplierOptions = (payload) => {
    const rows = Array.isArray(payload)
        ? payload
        : (payload?.thirdParties || payload?.suppliers || []);

    return rows
        .filter((supplier) => supplier?.id !== undefined && supplier?.id !== null)
        .map((supplier) => ({
            value: supplier.id,
            label: supplier.name || supplier.shortName || String(supplier.id),
        }));
};

const getDocumentFileName = (document) => {
    const candidates = [
        document?.fileName,
        document?.file_name,
        document?.originName,
        document?.origin_name,
        document?.filename,
        document?.name,
    ];

    return candidates.find((value) => (
        typeof value === 'string' && value.trim()
    )) || '';
};

const documentContentType = (document) => (
    document?.content_type || document?.contentType || ''
);

const getDocumentSupplierOption = (document) => {
    if (!document?.supplierId) {
        return null;
    }

    return {
        value: document.supplierId,
        label: document.supplierName || document.supplier_name || String(document.supplierId),
    };
};

const DocumentModal = ({
    t,
    isOpen,
    toggle,
    onComplete,
    onSubmit,
    titleHint,
    documentToEdit,
    supplierId,
    productId,
    askForSupplier = false,
    askForProduct = false,
    requireProduct = false,
    useOrganizationProducts = false,
    confidentialityLevels = ThirdPartyDocumentConfidentialityLevels,
    showLegalEntities = true,
    onActionDenied,
}) => {
    const [contentType, setContentType] = useState('file');
    const [category, setCategory] = useState('');
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [selectedLegalEntities, setSelectedLegalEntities] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [customSubmitLoading, setCustomSubmitLoading] = useState(false);

    const legalEntitiesQuery = useQuery({
        queryKey: ['3rd-party-management-document-legal-entities'],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchLegalEntities();
        },
        enabled: isOpen && showLegalEntities,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('An error occurred while fetching legal entities.'), {
                type: 'error',
            });
        },
    });

    const legalEntityOptions = useMemo(() => {
        const data = legalEntitiesQuery.data;
        const entities = Array.isArray(data)
            ? data
            : data?.legalEntities || [];

        return [
            {
                value: ALL_LEGAL_ENTITIES,
                label: t('All legal entities'),
            },
            ...entities.map(normalizeLegalEntity),
        ];
    }, [legalEntitiesQuery.data, t]);

    const showSupplierSelect = askForSupplier && !supplierId;

    const suppliersQuery = useQuery({
        queryKey: ['3rd-party-management-document-supplier-options'],
        queryFn: async () => {
            const params = { pageIndex: 1, pageSize: 100 };
            const [thirdParties, ownCompanies] = await Promise.all([
                ThirdpartyUsersService.getInstance().list(params),
                OwnThirdpartyUsersService.getInstance().list(params),
            ]);

            const options = [
                ...getSupplierOptions(thirdParties),
                ...getSupplierOptions(ownCompanies),
            ];
            const seen = new Set();

            return options.filter((option) => {
                if (seen.has(String(option.value))) {
                    return false;
                }

                seen.add(String(option.value));

                return true;
            });
        },
        enabled: isOpen && showSupplierSelect,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching third parties list.'), {
                type: 'error',
            });
        },
    });

    const supplierOptions = useMemo(() => {
        const options = suppliersQuery.data || [];
        const currentSupplier = getDocumentSupplierOption(documentToEdit);

        if (!currentSupplier) {
            return options;
        }

        const alreadyListed = options.some(
            (option) => String(option.value) === String(currentSupplier.value)
        );

        return alreadyListed ? options : [currentSupplier, ...options];
    }, [documentToEdit, suppliersQuery.data]);

    const productsQuery = useQuery({
        queryKey: [
            '3rd-party-management-document-product-options',
            supplierId || null,
            useOrganizationProducts,
        ],
        queryFn: async () => {
            if (useOrganizationProducts) {
                return await OrganizationProductsService.getInstance().list({
                    pageIndex: 1,
                    pageSize: 100,
                });
            }

            const service = EudrDeclarationService.getInstance();

            return await service.list(supplierId, {
                pageIndex: 1,
                pageSize: 100,
            });
        },
        enabled: isOpen && askForProduct,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching products list.'), {
                type: 'error',
            });
        },
    });

    const productOptions = useMemo(() => {
        const rows = Array.isArray(productsQuery.data?.products)
            ? productsQuery.data.products
            : [];
        const options = rows.map((product) => ({
            value: product.id,
            label: productLabel(product),
        }));
        const currentProduct = getDocumentProductOption(documentToEdit);

        if (!currentProduct) {
            return options;
        }

        const alreadyListed = options.some(
            (option) => String(option.value) === String(currentProduct.value)
        );

        return alreadyListed ? options : [currentProduct, ...options];
    }, [documentToEdit, productsQuery.data]);

    const showApiError = (error, fallback) => {
        const apiError = getDocumentApiError(error);

        if (apiError.fileMessage) {
            setFileError(apiError.fileMessage);
        }

        toast(apiError.message || t(fallback), {
            type: 'error',
        });

        if (apiError.status === 403) {
            onActionDenied?.(documentToEdit);
        }

        if (apiError.status === 404) {
            onComplete?.();
            toggle();
        }
    };

    const createMutation = useMutation({
        mutationFn: async (payload) => {
            const service = DocumentManagementService.getInstance();

            return await service.create(payload);
        },
        onSuccess: () => {
            toast(t('Document added successfully.'), {
                type: 'success',
            });
            onComplete?.();
            toggle();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            showApiError(error, 'An error occurred while adding the document.');
        },
    });

    const updateMutation = useMutation({
        mutationFn: async (payload) => {
            const service = DocumentManagementService.getInstance();

            return await service.update(documentToEdit.id, payload);
        },
        onSuccess: () => {
            toast(t('Document updated successfully.'), {
                type: 'success',
            });
            onComplete?.();
            toggle();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            showApiError(error, 'An error occurred while updating the document.');
        },
    });

    const appendLegalEntities = (formData) => {
        if (selectedLegalEntities.some((entity) => entity.value === ALL_LEGAL_ENTITIES)) {
            formData.append('legalEntities[0]', ALL_LEGAL_ENTITIES);

            return;
        }

        if (selectedLegalEntities.length === 0) {
            if (documentToEdit) {
                formData.append('legalEntities', '');
            }

            return;
        }

        selectedLegalEntities.forEach((entity, index) => {
            formData.append(`legalEntities[${index}]`, entity.value);
        });
    };

    const handleSubmit = async (event, values) => {
        event.preventDefault();
        setFileError('');

        // Validate file if contentType is 'file' and file is present
        if (contentType === 'file' && file) {
            const extension = file.name.split('.').pop()?.toLowerCase();

            if (!DOCUMENT_FILE_EXTENSIONS.includes(extension)) {
                setFileError(t('This file type is not allowed.'));
                return;
            }
            if (file.size > DOCUMENT_FILE_MAX_BYTES) {
                setFileError(t('The file must be 20 MB or smaller.'));
                return;
            }
        }

        const formData = new FormData();
        const original = documentToEdit || {};

        if (requireProduct && !selectedProduct?.value) {
            toast(t('Please select a product.'), {
                type: 'error',
            });
            return;
        }

        const appendField = (key, value) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value);
            }
        };

        // The update endpoint expects a submitted payload even when values
        // are unchanged. A locked file is deliberately excluded, so a
        // change-only payload can otherwise be empty.
        appendField('title', values?.title);
        appendField('description', values?.description);
        appendField('contentType', contentType);
        appendField('type', values?.type);
        appendField('category', category);
        appendField('confidentialityLevel', values?.confidentialityLevel);

        if (contentType === 'link') {
            appendField('path', values?.path);
        }

        if (contentType === 'file' && file) {
            formData.append('file', file);
        }

        if (showLegalEntities && (documentToEdit || selectedLegalEntities.length > 0)) {
            appendLegalEntities(formData);
        }

        // Supplier logic
        if (supplierId) {
            const supplierUnchanged = documentToEdit
                && String(supplierId) === String(original.supplierId ?? '');
            if (!supplierUnchanged) {
                formData.append('supplierId', supplierId);
            }
        } else if (showSupplierSelect) {
            const nextSupplierId = selectedSupplier?.value ?? '';
            const originalSupplierId = original.supplierId ?? '';
            if (!documentToEdit && nextSupplierId) {
                formData.append('supplierId', nextSupplierId);
            }
            if (documentToEdit && String(nextSupplierId) !== String(originalSupplierId)) {
                formData.append('supplierId', nextSupplierId);
            }
        }

        // Product logic
        const nextProductId = askForProduct ? selectedProduct?.value : productId;
        const originalProductId = original.productId || original.product_id || '';
        if (nextProductId && (
            !documentToEdit || String(nextProductId) !== String(originalProductId)
        )) {
            formData.append('productId', nextProductId);
        }

        // If custom onSubmit given:
        if (onSubmit) {
            setCustomSubmitLoading(true);
            try {
                await onSubmit(formData, documentToEdit);
                toast(t(documentToEdit ? 'Document updated successfully.' : 'Document added successfully.'), {
                    type: 'success',
                });
                onComplete?.();
                toggle();
            } catch (error) {
                if (process.env.NODE_ENV === 'development') console.error(error);
                showApiError(error, documentToEdit
                    ? 'An error occurred while updating the document.'
                    : 'An error occurred while adding the document.');
            } finally {
                setCustomSubmitLoading(false);
            }
            return;
        }

        if (documentToEdit) {
            updateMutation.mutate(formData);
        } else {
            createMutation.mutate(formData);
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        setFile(null);
        setFileError('');
        setContentType(documentToEdit?.content_type || documentToEdit?.contentType || 'file');
        setCategory(documentToEdit?.category || '');
        setSelectedSupplier(getDocumentSupplierOption(documentToEdit));
        setSelectedProduct(getDocumentProductOption(documentToEdit));
        setSelectedLegalEntities(
            (documentToEdit?.is_all_entities
                ? [{ value: ALL_LEGAL_ENTITIES, label: t('All legal entities') }]
                : (
                    documentToEdit?.legalEntities
                    || documentToEdit?.legal_entities
                    || []
                ).map(normalizeLegalEntity)
            )
        );
    }, [documentToEdit, isOpen, t]);

    const isSaving = createMutation.isLoading
        || updateMutation.isLoading
        || customSubmitLoading;
    const originalFileName = getDocumentFileName(documentToEdit);
    const hasExistingFile = Boolean(documentToEdit)
        && documentContentType(documentToEdit) === 'file';

    return (
        <Modal
            size="xl"
            isOpen={isOpen}
            toggle={toggle}
        >
            <ModalHeader toggle={toggle}>
                {t(documentToEdit ? 'Edit Document' : 'Add Document')}
            </ModalHeader>

            <AvForm
                key={documentToEdit?.id || 'new-document'}
                onValidSubmit={handleSubmit}
                className="needs-validation"
            >
                <ModalBody>
                    <Row>
                        <Col md="6">
                            <AvGroup className="mb-3">
                                <Label for="tpdd-document-title">
                                    {t('Title')}
                                </Label>
                                <AvField
                                    id="tpdd-document-title"
                                    name="title"
                                    type="text"
                                    className="form-control"
                                    validate={{
                                        required: { value: true },
                                    }}
                                    errorMessage={t('This field cannot be blank')}
                                    defaultValue={documentToEdit?.title}
                                />
                                {titleHint && (
                                    <small className="text-muted">
                                        {t(titleHint)}
                                    </small>
                                )}
                            </AvGroup>
                        </Col>
                        <Col md="6">
                            <AvGroup className="mb-3">
                                <Label for="tpdd-document-category">
                                    {t('Category')}
                                </Label>
                                <AvField
                                    id="tpdd-document-category"
                                    name="category"
                                    type="select"
                                    className="form-control"
                                    value={category}
                                    onChange={(event) => setCategory(event.target.value)}
                                    validate={{
                                        required: { value: true },
                                    }}
                                    errorMessage={t('This field cannot be blank')}
                                >
                                    <option value="">
                                        -- {t('Select Category')} --
                                    </option>
                                    {ThirdPartyDocumentCategories.map((item) => (
                                        <option
                                            key={item.value}
                                            value={item.value}
                                        >
                                            {t(item.title)}
                                        </option>
                                    ))}
                                </AvField>
                            </AvGroup>
                        </Col>
                    </Row>

                    <AvGroup className="mb-3">
                        <Label for="tpdd-document-description">
                            {t('Description')}
                        </Label>
                        <AvField
                            id="tpdd-document-description"
                            name="description"
                            type="textarea"
                            className="form-control"
                            rows="3"
                            defaultValue={documentToEdit?.description}
                        />
                    </AvGroup>

                    <Row>
                        <Col md="6">
                            <AvGroup className="mb-3">
                                <Label for="tpdd-document-content-type">
                                    {t('Content Type')}
                                </Label>
                                <AvField
                                    id="tpdd-document-content-type"
                                    name="contentType"
                                    type="select"
                                    className="form-control"
                                    value={contentType}
                                    onChange={(event) => {
                                        setContentType(event.target.value);
                                        setFile(null);
                                    }}
                                    validate={{
                                        required: { value: true },
                                    }}
                                    errorMessage={t('This field cannot be blank')}
                                >
                                    <option value="file">{t('File')}</option>
                                    <option value="link">{t('Link')}</option>
                                </AvField>
                            </AvGroup>
                        </Col>
                        <Col md="6">
                            <AvGroup className="mb-3">
                                <Label for="tpdd-document-type">
                                    {t('Type')}
                                </Label>
                                <AvField
                                    id="tpdd-document-type"
                                    name="type"
                                    type="select"
                                    className="form-control"
                                    defaultValue={documentToEdit?.type || 'internal'}
                                    validate={{
                                        required: { value: true },
                                    }}
                                    errorMessage={t('This field cannot be blank')}
                                >
                                    {ThirdPartyDocumentTypes
                                        .filter((item) => item.creatable)
                                        .map((item) => (
                                            <option
                                                key={item.value}
                                                value={item.value}
                                            >
                                                {t(item.title)}
                                            </option>
                                        ))}
                                </AvField>
                            </AvGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            {contentType === 'file' ? (
                                <FormGroup className="mb-3">
                                    <Label for="tpdd-document-file">
                                        {t('File Upload')}
                                    </Label>
                                    {hasExistingFile && originalFileName && (
                                        <div className="text-muted text-break mb-2">
                                            {originalFileName}
                                        </div>
                                    )}
                                    <div className="d-flex align-items-center">
                                        <Input
                                            type="file"
                                            name="documentFile"
                                            id="tpdd-document-file"
                                            className="d-none"
                                            accept={DOCUMENT_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
                                            onChange={(event) => {
                                                setFile(event.target.files?.[0] || null);
                                                setFileError('');
                                            }}
                                        />
                                        <label
                                            htmlFor="tpdd-document-file"
                                            className="btn btn-outline-primary me-2 mb-0"
                                        >
                                            {t('Choose File')}
                                        </label>
                                        {file && (
                                            <small className="text-muted text-break">
                                                {file.name}
                                            </small>
                                        )}
                                    </div>
                                    <AvField
                                        name="file"
                                        type="hidden"
                                        value={file?.name || ''}
                                        validate={{
                                            required: {
                                                value: !hasExistingFile,
                                            },
                                        }}
                                        errorMessage={t('This field cannot be blank')}
                                    />
                                    {fileError && (
                                        <div className="invalid-feedback d-block">
                                            {fileError}
                                        </div>
                                    )}
                                </FormGroup>
                            ) : (
                                <AvGroup className="mb-3">
                                    <Label for="tpdd-document-path">
                                        {t('Path')}
                                    </Label>
                                    <AvField
                                        id="tpdd-document-path"
                                        name="path"
                                        type="text"
                                        className="form-control"
                                        defaultValue={documentToEdit?.path}
                                        validate={{
                                            required: { value: true },
                                            pattern: {
                                                value: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                                                errorMessage: t('Please enter a valid URL'),
                                            },
                                        }}
                                        errorMessage={t('This field cannot be blank')}
                                    />
                                </AvGroup>
                            )}
                        </Col>
                        <Col md="6">
                            <AvGroup className="mb-3">
                                <Label for="tpdd-document-confidentiality">
                                    {t('Confidentiality Level')}
                                </Label>
                                <AvField
                                    id="tpdd-document-confidentiality"
                                    name="confidentialityLevel"
                                    type="select"
                                    className="form-control"
                                    defaultValue={
                                        documentToEdit?.confidentialityLevel || ''
                                    }
                                    validate={{
                                        required: { value: true },
                                    }}
                                    errorMessage={t('This field cannot be blank')}
                                >
                                    <option value="">
                                        -- {t('Select Level')} --
                                    </option>
                                    {confidentialityLevels.map((item) => (
                                        <option
                                            key={item.value}
                                            value={item.value}
                                        >
                                            {t(item.title)}
                                        </option>
                                    ))}
                                </AvField>
                            </AvGroup>
                        </Col>
                    </Row>

                    {(showSupplierSelect || askForProduct) && (
                        <Row>
                            {showSupplierSelect && (
                                <Col md="6">
                                    <AvGroup className="mb-3">
                                        <Label for="tpdd-document-supplier">
                                            {t('Third Party')}
                                        </Label>
                                        {suppliersQuery.isFetching ? (
                                            <div
                                                className="d-flex align-items-center form-control text-muted"
                                                role="status"
                                                aria-live="polite"
                                            >
                                                <Spinner
                                                    size="sm"
                                                    color="primary"
                                                    className="me-2"
                                                />
                                                {t('Loading third parties, please wait...')}
                                            </div>
                                        ) : (
                                            <Select
                                                inputId="tpdd-document-supplier"
                                                isClearable
                                                options={supplierOptions}
                                                value={selectedSupplier}
                                                onChange={(selected) => setSelectedSupplier(selected)}
                                                placeholder={`${t('Select')}...`}
                                                classNamePrefix="select2-selection"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: (base) => ({
                                                        ...base,
                                                        zIndex: 9999,
                                                    }),
                                                }}
                                            />
                                        )}
                                    </AvGroup>
                                </Col>
                            )}
                            {askForProduct && (
                                <Col md="6">
                                    <AvGroup className="mb-3">
                                        <Label for="tpdd-document-product">
                                            {t('Product')}{requireProduct ? ' *' : ''}
                                        </Label>
                                        {productsQuery.isFetching ? (
                                            <div
                                                className="d-flex align-items-center form-control text-muted"
                                                role="status"
                                                aria-live="polite"
                                            >
                                                <Spinner
                                                    size="sm"
                                                    color="primary"
                                                    className="me-2"
                                                />
                                                {t('Loading products, please wait...')}
                                            </div>
                                        ) : (
                                            <Select
                                                inputId="tpdd-document-product"
                                                isClearable={!requireProduct}
                                                options={productOptions}
                                                value={selectedProduct}
                                                onChange={(selected) => setSelectedProduct(selected)}
                                                placeholder={`${t('Select')}...`}
                                                classNamePrefix="select2-selection"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: (base) => ({
                                                        ...base,
                                                        zIndex: 9999,
                                                    }),
                                                }}
                                            />
                                        )}
                                    </AvGroup>
                                </Col>
                            )}
                        </Row>
                    )}

                    {showLegalEntities && (
                        <AvGroup className="mb-3">
                            <Label for="tpdd-document-legal-entities">
                                {t('Legal Entities')}
                            </Label>
                            <Select
                                inputId="tpdd-document-legal-entities"
                                isMulti
                                isLoading={legalEntitiesQuery.isFetching}
                                isDisabled={legalEntitiesQuery.isFetching}
                                options={legalEntityOptions}
                                value={selectedLegalEntities}
                                onChange={(selected) => {
                                    const nextSelection = selected || [];
                                    const includesAll = nextSelection.some(
                                        (entity) => entity.value === ALL_LEGAL_ENTITIES
                                    );

                                    setSelectedLegalEntities(
                                        includesAll
                                            ? nextSelection.filter((entity) => entity.value === ALL_LEGAL_ENTITIES)
                                            : nextSelection
                                    );
                                }}
                                placeholder={`${t('Select')}...`}
                                classNamePrefix="select2-selection"
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                            />
                        </AvGroup>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button
                        color="primary"
                        type="submit"
                        disabled={isSaving}
                    >
                        {isSaving && (
                            <Spinner
                                color="white"
                                size="sm"
                                className="me-1"
                            />
                        )}
                        {t(documentToEdit ? 'Update' : 'Add')}
                    </Button>
                    <Button
                        color="secondary"
                        type="button"
                        onClick={toggle}
                        disabled={isSaving}
                    >
                        {t('Cancel')}
                    </Button>
                </ModalFooter>
            </AvForm>
        </Modal>
    );
};

export default withNamespaces()(DocumentModal);
